var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { isEmpty, sortBy, uniqBy } from "../utils/nativeUtils";
import { action, configure, makeAutoObservable, observable, toJS } from "mobx";
import { createContext } from "react";
import config from "../config";
import { FilterOptionEnum, ServiceEnum } from "../models/dataModel";
import { hasSubcategories } from "../utils/functions";
import { i18nConfig } from "../i18nConfig";
configure({ isolateGlobalState: true, useProxies: "never", enforceActions: "observed" });
/**
 * Store for handling UI state
 */
export class UIStateStore {
    /**
     * Stores filters, used for making the autocomplete query.
     */
    autocompleteFilters;
    /**
     * Stores the facet states - if true, facet is open
     */
    facetStates = observable.map({
        product: false,
        software_version: false,
        area: false,
        content_area: false,
        content_type: true,
        type: false,
        information_type: false,
        environment: false,
        device: false,
    });
    /**
     * Stores the categories/services that have subfacets.
     * NOTE: should update this if more categories with subfacets are added!
     */
    categoriesWithSubfacets = [ServiceEnum.TRIMBLE_HELP];
    /**
     * Stores the open category, used for displaying index specific facets.
     */
    openCategory;
    /**
     * Stores the category options.
     */
    categoryOptions = [];
    /**
     * Stores the service in which the Search Client is embedded.
     */
    service;
    /**
     * Stores the language key.
     */
    languageKey;
    /**
     * Stores open category specific filters when category is changed
     */
    openCategorySpecificFilters = {};
    /**
     * Sidebar toggled state (mobile view)
     */
    sidebarToggled = false;
    constructor() {
        makeAutoObservable(this);
    }
    /**
     * Returns a list of autocomplete filters.
     * @returns filters
     */
    getAutocompleteFilters() {
        return this.autocompleteFilters;
    }
    /**
     * Sets the filters used in the autocomplete query
     * @param filters filters to use on onAutocomplete
     */
    setAutocompleteFilters(filters) {
        this.autocompleteFilters = filters;
    }
    /**
     * Returns stored category options
     * @returns category options
     */
    getSortedCategoryOptions() {
        return sortBy(this.categoryOptions, (option) => option.value);
    }
    /**
     * Sets the category options
     * If stored categoryOptions is empty, sets the options from props
     * If stored categoryOptions is not empty, combines the existing array with the new one,
     * keeping updated values from the props.
     * @param categoryOptions category options
     */
    setCategoryOptions(categoryOptions, categoryChanged) {
        if (isEmpty(this.categoryOptions)) {
            const initialCategory = categoryOptions.some((option) => option.selected) ? undefined : this.openCategory;
            this.categoryOptions = categoryOptions.map((option) => ({
                ...option,
                selected: option.selected || option.value === initialCategory,
            }));
        }
        else {
            // if options do not include info about facet counts or facet selection, return without assigning new options.
            if (!categoryOptions.find((o) => !!o.count || !!o.selected))
                return;
            const selected = categoryOptions.find((o) => o.selected);
            if (selected) {
                // If selected category doesn't change & given category has no subcategories, don't assign new options.
                if (!categoryChanged && !hasSubcategories(selected))
                    return;
                // if category is selected, update count - else keep old count
                categoryOptions = categoryOptions.map((option) => {
                    const oldValue = this.categoryOptions.find((o) => o.value === option.value);
                    return {
                        value: option.value,
                        count: option.selected ? option.count : oldValue?.count,
                        selected: option.selected,
                    };
                });
            }
            this.categoryOptions = uniqBy([...categoryOptions, ...toJS(this.categoryOptions)], "value");
        }
    }
    /**
     * Returns the language key
     */
    setLanguage(language) {
        this.languageKey = language;
    }
    /**
     * Returns the language key
     */
    getLanguage() {
        return this.languageKey;
    }
    /**
     * Returns the open category (in index form)
     * @returns open category key ("thlp" / "com")
     */
    getOpenCategory() {
        return Object.keys(config.services).find((serviceKey) => {
            const translatedCategoryName = i18nConfig.messages[this.languageKey || "en"][`facets.category.${serviceKey}`] ||
                i18nConfig.messages[i18nConfig.defaultLocale][`facets.category.${serviceKey}`];
            return translatedCategoryName === this.openCategory;
        });
    }
    /**
     * Returns the category key (in index form) based on given category name
     * @returns category key ("thlp" / "com")
     */
    getCategoryKeyBasedOnName(categoryName) {
        return Object.keys(config.services).find((serviceKey) => {
            const translatedCategoryName = i18nConfig.messages[this.languageKey || "en"][`facets.category.${serviceKey}`] ||
                i18nConfig.messages[i18nConfig.defaultLocale][`facets.category.${serviceKey}`];
            return translatedCategoryName === categoryName;
        });
    }
    /**
     * Sets the open category name
     * @param name open category name ("User Assistance", "Product Information")
     */
    setOpenCategory(name) {
        this.openCategory = name;
        if (!!name && !isEmpty(this.categoryOptions)) {
            // add 'selected' to open category, false for others
            const newOptions = this.categoryOptions.map((option) => {
                if (option.value === name)
                    return { ...option, selected: true };
                else
                    return { ...option, selected: false };
            });
            this.setCategoryOptions(newOptions, true);
        }
    }
    /**
     * Returns true if category has subfacets & is selected
     * NOTE: should update this if more categories with subfacets are added!
     * @param selected selected category key
     * @returns
     */
    categoryHasSubFacetsAndIsSelected(selected) {
        return this.getOpenCategory() === selected && this.categoriesWithSubfacets.includes(selected);
    }
    /**
     * Returns possible open filters for the given category
     * @returns list of open filters for the category
     */
    getOpenCategoryFilters(categoryName) {
        return !!this.openCategorySpecificFilters && !!this.openCategorySpecificFilters[categoryName]
            ? this.openCategorySpecificFilters[categoryName]
            : [];
    }
    /**
     * Sets open category filters for the open category
     */
    setOpenCategoryFilters(filters, clearAll) {
        if (clearAll) {
            this.openCategorySpecificFilters = {};
        }
        else {
            filters = filters.filter((filter) => filter.field !== FilterOptionEnum.CATEGORY);
            if (this.openCategory) {
                this.openCategorySpecificFilters = { ...this.openCategorySpecificFilters, [this.openCategory]: filters };
            }
        }
    }
    /**
     * Returns a list of available facets. If in global mode & a category is open,
     * this includes main facets and subfacets.
     * @returns list of available facets
     */
    getAvailableFacets() {
        if (!this.service)
            return;
        let facets = (this.inGlobalSearchMode() ? config.global : config.services[this.service]).facetList;
        const openCategory = this.getOpenCategory();
        if (!!facets && !!openCategory) {
            facets = facets.concat(config.services[openCategory].facetList || []);
        }
        return facets;
    }
    /**
     * Returns the selected service in which the Search Client is embedded.
     * @returns the selected service
     */
    getService() {
        return this.service;
    }
    /**
     * Checks if the given facet is currently open.
     * @param facetKey facet key
     */
    isFacetOpen(facetKey) {
        return !!this.facetStates.get(facetKey);
    }
    /**
     * Checks if the client is in global search mode
     * @returns true/false
     */
    inGlobalSearchMode() {
        const services = Object.keys(config.services);
        return !!this.service && services.includes(this.service);
    }
    /**
     * Returns the sidebar toggled state (mobile view)
     */
    isSidebarToggled() {
        return this.sidebarToggled;
    }
    /**
     * Toggles the sidebar state (mobile view)
     */
    toggleSidebar() {
        this.sidebarToggled = !this.sidebarToggled;
    }
    /**
     * Modifies given facet state to the given value.
     * @param facetKey facet key
     * @param state boolean that tells if facet is open or closed
     */
    setFacetState(facetKey, state) {
        this.facetStates.set(facetKey, state);
    }
    /**
     * Sets the selected service
     * @param service the selected service ("thlp", "com"...)
     */
    setSelectedService(service) {
        this.service = service;
    }
}
__decorate([
    observable
], UIStateStore.prototype, "autocompleteFilters", void 0);
__decorate([
    observable
], UIStateStore.prototype, "facetStates", void 0);
__decorate([
    observable
], UIStateStore.prototype, "openCategory", void 0);
__decorate([
    observable
], UIStateStore.prototype, "categoryOptions", void 0);
__decorate([
    observable
], UIStateStore.prototype, "service", void 0);
__decorate([
    observable
], UIStateStore.prototype, "languageKey", void 0);
__decorate([
    observable
], UIStateStore.prototype, "openCategorySpecificFilters", void 0);
__decorate([
    observable
], UIStateStore.prototype, "sidebarToggled", void 0);
__decorate([
    action
], UIStateStore.prototype, "setAutocompleteFilters", null);
__decorate([
    action
], UIStateStore.prototype, "setCategoryOptions", null);
__decorate([
    action
], UIStateStore.prototype, "setLanguage", null);
__decorate([
    action
], UIStateStore.prototype, "setOpenCategory", null);
__decorate([
    action
], UIStateStore.prototype, "setOpenCategoryFilters", null);
__decorate([
    action
], UIStateStore.prototype, "toggleSidebar", null);
__decorate([
    action
], UIStateStore.prototype, "setFacetState", null);
__decorate([
    action
], UIStateStore.prototype, "setSelectedService", null);
export const UIStateStoreContext = createContext(new UIStateStore());
